import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/InvoiceItemsService';
import { InvoiceItem } from '../types';

export const useInvoiceItem = (invoiceId: string, invoiceItemId?: string) => {
    if (!invoiceId) {
        throw new Error('invoiceId is required parameter');
    }

    const queryClient = useQueryClient();

    // FETCH
    const invoiceItemQuery = useQuery({
        queryKey: ['invoice-item', invoiceId, invoiceItemId],
        queryFn: () => fetch(invoiceId, invoiceItemId!),
        enabled: !!invoiceItemId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: InvoiceItem) => {
            const { created, updated, ...rest } = values;
            return add({ ...rest, invoice: invoiceId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoice-items', invoiceId] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: InvoiceItem) => {
            if (!invoiceItemId) throw new Error('invoiceItemId is required parameter');
            const { created, updated, ...rest } = values;
            return update(rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoice-items', invoiceId] });
            queryClient.invalidateQueries({ queryKey: ['invoice-item', invoiceId, invoiceItemId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!invoiceItemId) throw new Error('invoiceItemId is required parameter');
            return deleteRecord(invoiceId, invoiceItemId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invoice-items', invoiceId] });
        }
    });

    return {
        record: invoiceItemQuery.data,
        loading: invoiceItemQuery.isLoading,
        fetchRecord: invoiceItemQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};